import React from "react";
import { AREA_MAPS, MIGRATION_GAIN, MIGRATION_LOSS } from "../Layers/areaMaps";
import { heatmapSchemeColors } from "../Layers/colorSchemes";

// The small tiled maps of England and Wales drawn for each Big Picture map, on its card on the
// Big Picture page (BigPicture.js) and in the landing page's strip (BigPictureStrip.js)

// A rough tile map of England and Wales, '#' for land, for the cards' sample art
const TILES = [
    '      ##     ',
    '     ###     ',
    '     ####    ',
    '    #####    ',
    '    ######   ',
    '   #######   ',
    '   ########  ',
    ' ## ######## ',
    ' ### ########',
    ' ###  #######',
    '  ## ########',
    '   ##########',
    '  ########## ',
    ' ######  ##  ',
    '####         ',
];

// Repeatable made-up values, 0 to 1, so the art is the same every time
const noise = (x, y) => {
    const s = Math.sin(x*12.9898+y*78.233)*43758.5453;
    return s-Math.floor(s);
};

// Prices climb towards London in the south east; crime is patchier, higher in the cities
const SHADE_BY = {
    propertyLad: (x, y) => Math.min(1, 0.55*(x/12)+0.35*(y/14)+0.25*noise(x, y)),
    crimeLad: (x, y) => Math.min(1, 0.3+0.6*noise(x, y)*noise(y, x+3)+([[5, 8], [7, 6], [10, 12], [6, 3]].some(([cx, cy]) =>
        Math.abs(cx-x)+Math.abs(cy-y)<=1) ? 0.35 : 0)),
    // Deprivation is highest in the north and in pockets of the cities and coast
    deprivationLad: (x, y) => Math.min(1, 0.2+0.4*(1-y/14)+0.3*noise(y, x)+([[5, 8], [7, 6], [10, 12], [3, 12]].some(([cx, cy]) =>
        Math.abs(cx-x)+Math.abs(cy-y)<=1) ? 0.3 : 0)),
    // Rents peak in and around London, with a few dearer cities further out
    rentLad: (x, y) => Math.min(1, 0.15+0.75*Math.max(0, 1-Math.hypot(x-10, y-12)/9)+0.15*noise(x+1, y)+([[6, 3], [7, 10]].some(([cx, cy]) =>
        Math.abs(cx-x)+Math.abs(cy-y)<=0) ? 0.3 : 0)),
    // Homes change hands most along the coasts and in the shires, least in London
    salesRateLad: (x, y) => Math.min(1, Math.max(0, 0.35+0.35*noise(x+2, y+5)+(x<=3 || y<=2 ? 0.25 : 0)-
        0.6*Math.max(0, 1-Math.hypot(x-10, y-12)/3))),
    // Gigabit broadband is best in the cities, patchiest in the countryside and the far north
    connectivityLad: (x, y) => Math.min(1, Math.max(0, 0.25+0.3*noise(x+3, y+2)+([[5, 8], [7, 6], [10, 12], [6, 3], [3, 12]].some(([cx, cy]) =>
        Math.abs(cx-x)+Math.abs(cy-y)<=1) ? 0.4 : 0)-(y<=2 ? 0.2 : 0))),
    // People leave the big cities, London most, for the coasts and the countryside around them
    migrationLad: (x, y) => Math.min(1, Math.max(0, 0.6+0.3*noise(x+4, y+1)-0.65*Math.max(0, 1-Math.hypot(x-10, y-12)/2.5)-
        ([[5, 8], [7, 6], [6, 3]].some(([cx, cy]) => cx===x && cy===y) ? 0.45 : 0))),
};

// A map's art in colours of its own, rather than its palette's (the migration map's purple to green)
const TILE_COLORS = {
    migrationLad: MIGRATION_LOSS.slice().reverse().concat(MIGRATION_GAIN),
};

export const TileMap = ({layer}) => {
    const colors = TILE_COLORS[layer] || heatmapSchemeColors(AREA_MAPS[layer].palette || 'teal');
    const shade = SHADE_BY[layer] || noise;
    const cell = 14;
    return (
        <svg className="bp-tiles" viewBox={'0 0 '+(13*cell)+' '+(TILES.length*cell)} aria-hidden="true">
            {TILES.flatMap((row, y) => [...row].map((c, x) => c!=='#' ? null :
                <rect key={x+','+y} x={x*cell+1} y={y*cell+1} width={cell-2} height={cell-2} rx="2.5"
                    fill={colors[Math.min(colors.length-1, Math.floor(shade(x, y)*colors.length))]}/>))}
        </svg>
    );
};

// The top 10's map: every area a faint outline, with a few rising (green) and falling (rust) ones filled in
const MOVERS = {'6,2':'up', '9,7':'up', '3,10':'up', '11,11':'up', '9,13':'up', '5,5':'down', '10,9':'down', '2,8':'down', '7,11':'down'};
const MOVER_COLORS = {up:'#2f9a6f', down:'#d0533c'};
export const MoversMap = () => {
    const cell = 14;
    return (
        <svg className="bp-tiles" viewBox={'0 0 '+(13*cell)+' '+(TILES.length*cell)} aria-hidden="true">
            {TILES.flatMap((row, y) => [...row].map((c, x) => {
                if (c!=='#') return null;
                const mover = MOVERS[x+','+y];
                return <rect key={x+','+y} x={x*cell+1.5} y={y*cell+1.5} width={cell-3} height={cell-3} rx="2.5"
                    fill={mover ? MOVER_COLORS[mover] : '#fff'} stroke={mover ? 'none' : '#c3cfca'} strokeWidth="1"/>;
            }))}
        </svg>
    );
};
